import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../UI/Buttons';
import { Text } from '../../UI/Typography';
import { FaTimes, FaSpinner, FaSearch } from 'react-icons/fa';
import { FaTrash } from 'react-icons/fa';
import { addEmployeesToDepartment, getDepartmentEmployees, removeEmployeesFromDepartment } from '../../../Services/Api/departmentService';
import { getAllUsers } from '../../../Services/Api/userService';
import { usePopup } from '../../../Popups/PopupContext';
import { useAuth } from '../../../Context/AuthContext';

const AddEmployeeModal = ({ isOpen, onClose, departmentId, departmentName, onAdded }) => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const { showError, showSuccess } = usePopup();
  const { user } = useAuth();
  const [deleteMode, setDeleteMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    // ensure modal always opens in add mode
    setDeleteMode(false);
    setSelected([]);
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Derive companyId from various possible shapes
      let companyId = user?.company?.id || user?.company_id || user?.company?.company_id;
      if (!companyId) {
        const userStr = localStorage.getItem('userData');
        if (userStr) {
          try {
            const parsed = JSON.parse(userStr);
            companyId = parsed?.company?.id || parsed?.company_id || parsed?.company?.company_id;
          } catch (e) {}
        }
      }

      if (!companyId) {
        showError('Company missing', 'Unable to determine company ID to load users.');
        setUsers([]);
        return;
      }

      // Ensure `accessToken` exists for getAllUsers (some parts of app use `token` instead)
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        const alt = localStorage.getItem('token');
        if (alt) localStorage.setItem('accessToken', alt);
      }

      const data = await getAllUsers(companyId);
      setUsers(Array.isArray(data) ? data : (data?.users || []));
    } catch (err) {
      showError('Failed to load users', err.message || String(err));
      setUsers([]);
    } finally { setLoading(false); }
  };

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]);
  const selectAll = () => {
    const list = filteredUsers().map(u=>u.id);
    if (selected.length === list.length) setSelected([]); else setSelected(list);
  };

  const loadDepartmentMembers = async () => {
    if (!departmentId) return showError('Missing department', 'Department ID not provided');
    try {
      setLoading(true);
      const members = await getDepartmentEmployees(departmentId);
      const ids = Array.isArray(members) ? members.map(m => m.id) : [];
      setSelected(ids);
      setDeleteMode(true);
    } catch (err) {
      showError('Failed to load department members', err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  const deleteSelected = async () => {
    if (!selected.length) { showError('No selection','Please select at least one user to delete'); return; }
    setIsDeleting(true);
    try {
      await removeEmployeesFromDepartment(departmentId, selected);
      showSuccess('Removed','Employees removed successfully');
      if (onAdded) onAdded();
      onClose();
    } catch (err) {
      showError('Remove failed', err.message || String(err));
    } finally { setIsDeleting(false); }
  };

  const filteredUsers = () => {
    if (!search.trim()) return users;
    const s = search.toLowerCase();
    return users.filter(u => (u.first_name||'').toLowerCase().includes(s) || (u.username||'').toLowerCase().includes(s) || (u.email||'').toLowerCase().includes(s));
  };

  const submit = async () => {
    if (!selected.length) { showError('No selection','Please select at least one user'); return; }
    setSubmitting(true);
    try {
      await addEmployeesToDepartment(departmentId, selected);
      showSuccess('Added','Employees added successfully');
      if (onAdded) onAdded();
      onClose();
    } catch (err) {
      showError('Add failed', err.message || String(err));
    } finally { setSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={onClose} style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
        <motion.div onClick={(e)=>e.stopPropagation()} initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.9,opacity:0}} style={{background:'white',borderRadius:12,padding:20,maxWidth:700,width:'90%',maxHeight:'80vh',overflow:'auto'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <div>
              <Text variant="h2" style={{margin:0}}>{deleteMode ? `Delete users from ${departmentName}` : `Add Employees to ${departmentName}`}</Text>
              <Text variant="body2" style={{color:'#666'}}>{selected.length} selected</Text>
            </div>
            <Button variant="ghost" size="small" onClick={onClose}><FaTimes /></Button>
          </div>

          <div style={{marginBottom:12, position:'relative'}}>
            <input placeholder="Search users..." value={search} onChange={e=>setSearch(e.target.value)} style={{width:'100%',padding:'10px 10px 10px 36px',border:'1px solid #ddd',borderRadius:6}} />
            <FaSearch style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'#999'}} />
          </div>

          {loading ? (
            <div style={{padding:40,display:'flex',justifyContent:'center'}}><FaSpinner className="spin" size={28} /></div>
          ) : (
            <div style={{border:'1px solid #eee',borderRadius:8,padding:0,maxHeight:380,overflow:'auto'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 12px'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <Text variant="body2" style={{margin:0,display:'inline-flex',alignItems:'center'}}>{filteredUsers().length} users</Text>
                  <Button variant="ghost" size="small" onClick={selectAll} style={{height:28,padding:'4px 8px',display:'inline-flex',alignItems:'center'}}>{selected.length === filteredUsers().length ? 'Deselect all' : 'Select all'}</Button>
                </div>
                <div>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={loadDepartmentMembers}
                    title="Select users to delete"
                    style={{height:28,padding:'4px 8px', borderColor: '#e53935'}}
                  >
                    <FaTrash style={{ color: '#e53935' }} />
                  </Button>
                </div>
              </div>

              {filteredUsers().length === 0 ? (
                // show placeholder empty rows like the design
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={`empty-${i}`} style={{height:48,display:'flex',alignItems:'center',padding:'8px 12px',borderTop:'1px solid #f2f2f2'}}>
                    <div style={{width:22,height:22,border:'1px solid #e0e0e0',borderRadius:4,background:'#fff',marginRight:12}} />
                    <div style={{height:14,background:'#f6f6f6',width:'100%',borderRadius:2}} />
                  </div>
                ))
              ) : (
                filteredUsers().map(u => (
                  <div key={u.id} onClick={() => toggle(u.id)} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 12px',borderTop:'1px solid #f2f2f2',cursor:'pointer'}}>
                    <input
                      type="checkbox"
                      checked={selected.includes(u.id)}
                      onChange={() => toggle(u.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{width:20,height:20,flex:'0 0 20px',borderRadius:4}}
                    />
                    <div style={{flex:1,paddingLeft:4}}>
                      <Text style={{margin:0,fontWeight:500}}>{u.first_name ? `${u.first_name} ${u.last_name||''}`.trim() : u.username || `User ${u.id}`}</Text>
                      {u.email && <Text variant="body2" style={{color:'#666'}}>{u.email}</Text>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

            <div style={{display:'flex',justifyContent:'flex-end',gap:12,marginTop:12}}>
            <Button variant="secondary" onClick={() => { setDeleteMode(false); onClose(); }} disabled={submitting || isDeleting}>Cancel</Button>
            {deleteMode ? (
              <Button
                variant="danger"
                onClick={deleteSelected}
                disabled={isDeleting || !selected.length}
                style={{ backgroundColor: '#e53935', color: '#fff', borderColor: '#e53935' }}
              >
                {isDeleting ? 'Removing...' : `Remove ${selected.length} Selected`}
              </Button>
            ) : (
              <Button variant="primary" onClick={submit} disabled={submitting || !selected.length}>{submitting ? 'Adding...' : `Add ${selected.length} Employee${selected.length!==1?'s':''}`}</Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddEmployeeModal;
